import { useId } from 'react';
import { t } from '@lingui/macro';
import styled from 'styled-components';

import {
  IconWrapper,
  SELECT_BUTTON_IS_SELECTED_CLASS,
  SelectButtonLabel,
  Wrapper,
} from '@rover/components/src/buttons/SelectButton';
import ButtonGroup from '@rover/components/src/formFields/ButtonGroup';
import Fieldset from '@rover/components/src/formFields/Fieldset';
import type { Option } from '@rover/components/src/formFields/MultiSelectButtonGroup';
import MultiSelectButtonGroup from '@rover/components/src/formFields/MultiSelectButtonGroup';
import { StyledCheckmark } from '@rover/components/src/formFields/MultiSelectButtonGroup/MultiSelectButtonGroup';
import DogSizeGiant from '@rover/icons/svg/dog-size-giant.svg';
import DogSizeLarge from '@rover/icons/svg/dog-size-large.svg';
import DogSizeMedium from '@rover/icons/svg/dog-size-medium.svg';
import DogSizeSmall from '@rover/icons/svg/dog-size-small.svg';
import { Paragraph } from '@rover/kibble/core';
import MQ from '@rover/kibble/styles/MediaQuery';
import type { ThemeTextColor } from '@rover/kibble/styles/themeTypes';
import type { ApiFrontendConfigurationRetrieve200SettingsDogSizeBuckets } from '@rover/rsdk/src/apiClient/latest/model/apiFrontendConfigurationRetrieve200SettingsDogSizeBuckets';
import FilterLabel from '@rover/shared/js/search/components/FilterLabel';

const StyledMultiSelectButtonGroup = styled(MultiSelectButtonGroup)`
  & ${Wrapper} {
    ${MQ.MD_UP.toString()} {
      flex-direction: column;
      justify-content: center;
    }
  }

  & ${SelectButtonLabel} {
    padding: ${({ theme }) => theme.space['4x']};

    ${MQ.MD_UP.toString()} {
      justify-content: center;
    }
  }

  & ${IconWrapper} {
    ${MQ.MD_UP.toString()} {
      margin-bottom: 6px;
      margin-right: 0px;
    }
  }
`;

const HomePageButtonGroup = styled(StyledMultiSelectButtonGroup)`
  & ${StyledCheckmark} {
    display: none;
  }
  & ${SelectButtonLabel} {
    height: 64px;
    padding: 0;

    &.${SELECT_BUTTON_IS_SELECTED_CLASS} {
      color: ${({ theme }) => theme.textColor.primary};
    }
  }
`;

interface Props {
  displayAsIconButtons?: boolean;
  displayAsHomePageSearchBoxButtons?: boolean;
  label: string;
  labelColor?: ThemeTextColor;
  subLabel?: string;
  onChange: (sizeKey: string | number) => void;
  petSizeBuckets: ApiFrontendConfigurationRetrieve200SettingsDogSizeBuckets;
  searchFilters: {
    giantDogs?: boolean;
    largeDogs?: boolean;
    mediumDogs?: boolean;
    smallDogs?: boolean;
  };
  weightUnit: string;
  showCheckmark?: boolean;
}

interface BasicOption {
  key: string;
  label: string;
  value: string;
}

type IconOption = Option & {
  icon: string;
  key: string;
  title: string;
  subtitle: string;
  displayAs: 'expanded';
};

const createOptions = (
  buckets: ApiFrontendConfigurationRetrieve200SettingsDogSizeBuckets,
  searchFilters: Props['searchFilters']
): BasicOption[] =>
  Object.keys(buckets).map((key) => ({
    key: `${key}Dogs`,
    label: buckets[key],
    value: searchFilters[`${key}Dogs`],
  }));

const createIconOptions = (
  buckets: ApiFrontendConfigurationRetrieve200SettingsDogSizeBuckets,
  weightUnit: Props['weightUnit']
): IconOption[] => {
  const PET_SIZE_CHOICES = {
    giant: { icon: DogSizeGiant, title: t`Giant` },
    large: { icon: DogSizeLarge, title: t`Large` },
    medium: { icon: DogSizeMedium, title: t`Medium` },
    small: { icon: DogSizeSmall, title: t`Small` },
  };

  return Object.keys(buckets).map((key) => {
    const { title } = PET_SIZE_CHOICES[key];
    const sizeBucket = buckets[key];
    const [lowerWeight, upperWeight] = sizeBucket.split('-');

    return {
      icon: PET_SIZE_CHOICES[key].icon,
      key: `${key}Dogs`,
      title,
      subtitle: `${sizeBucket.replace('-', ' - ')} ${weightUnit}`,
      displayAs: 'expanded',
      ariaLabel: t({
        message: `${title} (${lowerWeight} to ${upperWeight} ${weightUnit})`,
        comment:
          "this is an aria-label for a weight range selection button, ex: 'Small (0 to 15 lbs)'. ",
      }),
    };
  });
};

const createIconOptionsWithoutIcons = (
  buckets: ApiFrontendConfigurationRetrieve200SettingsDogSizeBuckets,
  weightUnit: Props['weightUnit']
): Array<Omit<IconOption, 'icon'>> =>
  // NOTE(Ryan) This feels like a perfectly acceptable usecase for an "unused" var
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  createIconOptions(buckets, weightUnit).map(({ icon, ...other }) => other);

const PetSize = ({
  displayAsHomePageSearchBoxButtons,
  displayAsIconButtons,
  label,
  labelColor = 'secondary',
  subLabel,
  onChange,
  petSizeBuckets,
  searchFilters,
  weightUnit,
  showCheckmark = true,
}: Props): JSX.Element | null => {
  const petSizeSelectorId = useId();

  if (!petSizeBuckets) return null;

  let buttons: JSX.Element;
  if (displayAsIconButtons) {
    buttons = (
      <StyledMultiSelectButtonGroup
        onChange={onChange}
        options={createIconOptions(petSizeBuckets, weightUnit)}
        selected={searchFilters}
        showCheckmark={showCheckmark}
      />
    );
  } else if (displayAsHomePageSearchBoxButtons) {
    buttons = (
      <HomePageButtonGroup
        onChange={onChange}
        options={createIconOptionsWithoutIcons(petSizeBuckets, weightUnit)}
        selected={searchFilters}
        showCheckmark={showCheckmark}
      />
    );
  } else {
    buttons = (
      <ButtonGroup
        onToggle={(option) => onChange(option.key)}
        options={createOptions(petSizeBuckets, searchFilters)}
      />
    );
  }

  return (
    petSizeBuckets && (
      <Fieldset aria-labelledby={petSizeSelectorId}>
        <FilterLabel id={petSizeSelectorId} as="div" color={labelColor}>
          {label}
        </FilterLabel>
        {subLabel && (
          <Paragraph size="100" mb="1x" textColor="tertiary">
            {subLabel}
          </Paragraph>
        )}
        {buttons}
      </Fieldset>
    )
  );
};

export default PetSize;
